import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import "../App.css";
import "../style/responsive.css";
import { FaEye, FaEyeSlash, FaUser, FaLock, FaCheck, FaExclamation } from "react-icons/fa";
import axiosInstance from "../utils/axiosConfig";
import Swal from "sweetalert2";
import logo from "../assets/images/cropped_circle_image.png";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    loginIdentifier: "", // Changed from fullName to loginIdentifier
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const usernameWithSpaceRegex = /^[A-Za-z]+(\s+[A-Za-z]+)+$/;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Validate login identifier (username or email)
    if (!formData.loginIdentifier.trim()) {
      newErrors.loginIdentifier = "Username or Email is required";
      isValid = false;
    } else {
      // If it looks like an email, validate email format
      if (formData.loginIdentifier.includes("@")) {
        if (!emailRegex.test(formData.loginIdentifier)) {
          newErrors.loginIdentifier =
            "Invalid email format (e.g. example@gmail.com)";
          isValid = false;
        }
        else if (!formData.loginIdentifier.endsWith("@gmail.com")) {
          newErrors.loginIdentifier = "Only Gmail addresses are allowed";
          isValid = false;
        }
      } else {
        // Validate username (must contain space)
        if (!usernameWithSpaceRegex.test(formData.loginIdentifier.trim())) {
          newErrors.loginIdentifier = "Username must contain a space (e.g. John Dee)";
          isValid = false;
        }

      }
    }

    // Validate password
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsLoading(true);
  setErrors({});

  if (validateForm()) {
    try {
      // Determine if loginIdentifier is email or username
      const isEmail = formData.loginIdentifier.includes("@");
      const loginData = {
        loginIdentifier: formData.loginIdentifier,
        password: formData.password,
        loginType: isEmail ? "email" : "username"
      };

      const response = await axiosInstance.post("/auth/login", loginData);

      if (response.data.success && response.data.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("refreshToken", response.data.refreshToken);

        login({
          _id: response.data.user._id,
          fullName: response.data.user.fullName,
        });

        // ✅ Store user data INCLUDING EMAIL
        localStorage.setItem("userId", response.data.user._id);
        localStorage.setItem("userFullName", response.data.user.fullName);
        localStorage.setItem("userEmail", response.data.user.email || ""); // ✅ ADD THIS LINE
        localStorage.setItem("userPhone", response.data.user.phoneNumber || "");
        localStorage.setItem("userAddress", response.data.user.address || "");

        localStorage.setItem(
          "user",
          JSON.stringify({
            id: response.data.user._id,
            fullName: response.data.user.fullName,
            email: response.data.user.email || "", // ✅ ADD THIS TOO
            phoneNumber: response.data.user.phoneNumber,
            address: response.data.user.address,
          })
        );

        Swal.fire({
          icon: "success",
          title: "Login Successful",
          text: "Welcome back!",
          showConfirmButton: false,
          timer: 1500,
        });

        navigate("/home");
      } else {
        setErrors({
          login: response.data.message || "Login failed",
        });
      }
      } catch (error) {
        if (error.response && error.response.data) {
          const errorMessage = error.response.data.message;

          // Set specific errors based on the backend response
          if (errorMessage === "Email not found") {
            setErrors({
              loginIdentifier: "Email not found",
              password: "",
            });
          } else if (errorMessage === "Username not found") {
            setErrors({
              loginIdentifier: "Username not found",
              password: "",
            });
          } else if (errorMessage === "Incorrect password") {
            setErrors({
              loginIdentifier: "",
              password: "Incorrect password",
            });
          } else if (errorMessage === "Invalid email format") {
            setErrors({
              loginIdentifier: "Invalid email format",
              password: "",
            });
          } else if (errorMessage === "Only Gmail addresses are allowed") {
            setErrors({
              loginIdentifier: "Only Gmail addresses are allowed",
              password: "",
            });
          } else {
            // Generic error
            setErrors({
              login: errorMessage || "Login failed",
            });
          }
        } else if (error.request) {
          setErrors({
            login: "No response from server. Please check your connection.",
          });
        } else {
          setErrors({
            login: "An unexpected error occurred. Please try again.",
          });
        }
      }
      finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const navigateToSignup = () => {
    navigate("/signup");
  };

  const navigateToForgotPassword = () => {
    navigate("/forgot-password");
  };

  const isValidEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return (
      emailRegex.test(formData.loginIdentifier) &&
      formData.loginIdentifier.endsWith("@gmail.com")
    );
  };

  const isValidUsername = () => {
    return usernameWithSpaceRegex.test(formData.loginIdentifier.trim());
  };

  const shouldShowValidationIcon = () => {
    return formData.loginIdentifier.length > 0;
  };

  const isValidPassword = () => {
    return formData.password.length >= 8;
  };

  const shouldShowPasswordValidationIcon = () => {
    return formData.password.length > 0;
  };

  return (
    <div className="signup-container" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
      <div className="signup-form">
        <div className="login-header">
          <div className="logo-container">
            <img src={logo} alt="Company Logo" className="logo" />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="input-group">
            <div className="input-icon mt-t">
              <FaUser />
            </div>

            <input
              type="text"
              name="loginIdentifier"
              className={`form-input ${errors.loginIdentifier
                ? "error"
                : shouldShowValidationIcon() &&
                  (isValidEmail() || isValidUsername())
                  ? "success"
                  : ""
                }`}
              placeholder="Enter username or email"
              value={formData.loginIdentifier}
              onChange={handleChange}
            />

            {/* Validation Icon */}
            {shouldShowValidationIcon() && (
              <span className="validation-icon">
                {errors.login ? (
                  <FaExclamation className="text-danger" />
                ) : isValidEmail() || isValidUsername() ? (
                  <FaCheck className="text-success" />
                ) : (
                  <FaExclamation className="text-danger" />
                )}
              </span>
            )}

            {errors.loginIdentifier && (
              <span className="error-message">{errors.loginIdentifier}</span>
            )}
          </div>

          <div className="input-group">
            <div className="input-icon mt-t">
              <FaLock />
            </div>

            <input
              type={showPassword ? "text" : "password"}
              name="password"
              className={`form-input ${errors.password
                ? "error"
                : shouldShowPasswordValidationIcon() && isValidPassword()
                  ? "success"
                  : ""
                }`}
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
            />

            {/* Password visibility toggle */}
            <span
              className="password-toggle"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>

            {shouldShowPasswordValidationIcon() && (
              <span className="validation-icon password-icon">
                {errors.login ? (
                  <FaExclamation className="text-danger" />
                ) : isValidPassword() ? (
                  <FaCheck className="text-success" />
                ) : (
                  <FaExclamation className="text-danger" />
                )}
              </span>
            )}


            {errors.password && (
              <span className="error-message">{errors.password}</span>
            )}
          </div>

          {errors.login && (
            <div className="alert alert-error">
              {errors.login}
            </div>
          )}

          <div className="forgot-password-link">
            <span onClick={navigateToForgotPassword}>
              Forgot password?
            </span>
          </div>

          <button
            type="submit"
            className={`login-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="spinner"></div>
            ) : (
              "Log In"
            )}
          </button>

          <div className="signup-redirect">
            <p>Don't have an account? <span onClick={navigateToSignup}>Sign Up</span></p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;

//Correct with 349 line code changes
